import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Box, CircularProgress, IconButton, Stack, TextField, Typography } from '@mui/material';
import { Pen, Truncate } from '@vy/ui-design-system';
import { isNotNullOrUndefined, vyDate } from '@vy/utils';
import { useState } from 'react';
import { formatDate } from '../../../../utils/date';
import { VyDescriptionTitle } from '../VyDescriptionTitle/VyDescriptionTitle';
import { VyPaper } from '../VyPaper/VyPaper';

type NoteProps = {
  id: string;
  isEditMode: boolean;
  content: string;
  canEdit: boolean;
  author: string;
  createdAt: string;
  toggleViewMode: () => void;
  onSave: (noteId: string, updatedText: string) => void;
  onDelete: () => void;
  updatedAt?: string | null;
  isLoading?: boolean;
  errorMessage?: string | null;
};

export const VyNote: React.FC<NoteProps> = ({
  id,
  content,
  author,
  createdAt,
  onSave,
  onDelete,
  toggleViewMode,
  isEditMode,
  isLoading,
  canEdit,
  updatedAt,
  errorMessage,
}) => {
  const [editableContent, setEditableContent] = useState(content);
  const isContentEmpty = editableContent.trim().length === 0;
  const hasError = isContentEmpty || !!errorMessage;

  return (
    <VyPaper sx={{ backgroundColor: '#F9FAFB', boxShadow: 'none' }} elevation={0}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 1
        }}>
        <VyDescriptionTitle
          title={author}
          subTitle={createdAt}
          icon={`${author.charAt(0).toUpperCase()}`}
          avatarSx={{ backgroundColor: '#2B7FFF', height: 28, width: 28, fontSize: '12px' }}
        />
        {isEditMode ? (
          <EditModeControls
            onSaveClick={() => onSave(id, editableContent.trim())}
            onCancelClick={() => {
              toggleViewMode();
              setEditableContent(content);
            }}
            disabled={isLoading || isContentEmpty}
          />
        ) : (
          <NoteControls
            canEdit={canEdit}
            onEditClick={toggleViewMode}
            onDeleteClick={onDelete}
            disabled={isLoading}
          />
        )}
      </Stack>
      <Box
        sx={{
          flexGrow: 1,
          maxWidth: "80%"
        }}>
        {isEditMode ? (
          <TextField
            label="Edit note"
            multiline
            maxRows={4}
            variant="standard"
            autoFocus
            onFocus={(event) => {
              const input = event.target as HTMLTextAreaElement;
              input.setSelectionRange(input.value.length, input.value.length);
            }}
            sx={{
              width: '100%',
            }}
            value={editableContent}
            onChange={(event) => setEditableContent(event.target.value)}
            error={hasError}
            helperText={isContentEmpty ? 'Note content is required' : errorMessage}
          />
        ) : (
          <Typography sx={{
            fontSize: "14px"
          }}>{content}</Typography>
        )}
      </Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: "flex-end",
          width: "100%",
          gap: 2
        }}>
        {isNotNullOrUndefined(updatedAt) && (
          <Typography
            title={formatDate(vyDate(updatedAt), 'MMM. D, YY HH:mm')}
            sx={{
              fontSize: 12,
              fontStyle: "italic",
              color: "text.secondary"
            }}>
            (edited)
          </Typography>
        )}
        {isLoading && <CircularProgress size={18} />}
      </Stack>
    </VyPaper>
  );
};

function NoteControls({
  canEdit,
  onEditClick,
  onDeleteClick,
  disabled,
}: {
  canEdit: boolean;
  onEditClick: () => void;
  onDeleteClick: () => void;
  disabled?: boolean;
}) {
  if (!canEdit) {
    return null;
  }

  return (
    <Stack direction="row">
      <IconButton onClick={onEditClick} disabled={disabled}>
        <Box
          sx={{
            path: {
              stroke: '#6A7282',
            },
          }}
        >
          <Pen />
        </Box>
      </IconButton>
      <IconButton onClick={onDeleteClick} disabled={disabled}>
        <Box
          sx={{
            path: {
              stroke: '#6A7282',
            },
          }}
        >
          <Truncate />
        </Box>
      </IconButton>
    </Stack>
  );
}

function EditModeControls({
  onSaveClick,
  onCancelClick,
  disabled,
}: {
  onSaveClick: () => void;
  onCancelClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Stack direction="row" spacing={1}>
      <IconButton onClick={onSaveClick} disabled={disabled}>
        <CheckOutlinedIcon style={{ color: '#6A7282' }} />
      </IconButton>
      <IconButton onClick={onCancelClick} disabled={disabled}>
        <CloseIcon style={{ color: '#6A7282' }} />
      </IconButton>
    </Stack>
  );
}
